import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { errText } from "./resourceApi";

function ActionDialog({ title, description, confirmLabel, testId, canConfirm = true, onConfirm, onClose, children, tone }) {
  const [busy, setBusy] = useState(false);
  async function go() {
    setBusy(true);
    try { await onConfirm(); onClose(); } catch (e) { toast.error(`${title} failed`, { description: errText(e) }); } finally { setBusy(false); }
  }
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid={testId}>
        <DialogHeader><DialogTitle>{title}</DialogTitle>{description && <DialogDescription>{description}</DialogDescription>}</DialogHeader>
        <div className="space-y-3">{children}</div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid={`${testId}-cancel`}>Cancel</Button>
          <Button onClick={go} disabled={!canConfirm || busy} data-testid={`${testId}-confirm`}
                  className={tone === "danger" ? "bg-red-600 hover:bg-red-700" : ""}>
            {busy && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}{confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Notes = ({ label, value, onChange, testId }) => (
  <div><Label className="text-xs">{label}</Label>
    <Textarea className="mt-1" rows={3} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} /></div>
);

export function UploadVersionDialog({ resource, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [summary, setSummary] = useState("");
  return (
    <ActionDialog title="Upload a new version" testId="upload-version-dialog" confirmLabel="Upload as draft" canConfirm={!!file}
                  description="The file becomes a new immutable draft version. The current approved version stays in use until a clinical approver approves this one."
                  onClose={onClose}
                  onConfirm={async () => {
                    const fd = new FormData();
                    fd.append("file", file);
                    fd.append("change_summary", summary);
                    await api.post(`/clinical-resources/${resource.id}/versions`, fd, { headers: { "Content-Type": "multipart/form-data" } });
                    toast.success("New draft version uploaded");
                    onDone();
                  }}>
      <Input type="file" accept="application/pdf,.docx,image/png,image/jpeg" onChange={(e) => setFile(e.target.files?.[0] || null)}
             data-testid="upload-version-file" />
      <Notes label="What changed?" value={summary} onChange={setSummary} testId="upload-version-summary" />
    </ActionDialog>
  );
}

export function SubmitReviewDialog({ resource, onClose, onDone }) {
  const [notes, setNotes] = useState("");
  return (
    <ActionDialog title="Send for clinical review" testId="submit-review-dialog" confirmLabel="Send for review" onClose={onClose}
                  description={resource.clinical_reviewer_name ? `${resource.clinical_reviewer_name} will be notified.` : "All clinical approvers will be notified."}
                  onConfirm={async () => {
                    await api.post(`/clinical-resources/${resource.id}/submit-review`, { notes });
                    toast.success("Sent for clinical review");
                    onDone();
                  }}>
      <Notes label="Notes for the reviewer (optional)" value={notes} onChange={setNotes} testId="submit-review-notes" />
    </ActionDialog>
  );
}

export function DecisionDialog({ resource, version, decision, onClose, onDone }) {
  const [notes, setNotes] = useState("");
  const [nextReview, setNextReview] = useState("");
  const approve = decision === "approve";
  return (
    <ActionDialog title={approve ? `Approve version ${version.version_number}` : "Request changes"} testId="decision-dialog"
                  confirmLabel={approve ? "Approve" : "Request changes"} canConfirm={approve || notes.trim().length >= 3} onClose={onClose}
                  description={approve ? "Approved versions can be sent to clients. Any earlier approved version becomes superseded — clients keep the exact version they were given."
                    : "The version goes back to Draft with your notes."}
                  onConfirm={async () => {
                    await api.post(`/clinical-resources/${resource.id}/approve`, {
                      version_id: version.id, decision, notes, next_review_date: nextReview || null,
                    });
                    toast.success(approve ? "Version approved" : "Changes requested");
                    onDone();
                  }}>
      <Notes label={approve ? "Approval notes (optional)" : "What needs to change?"} value={notes} onChange={setNotes} testId="decision-notes" />
      {approve && version.meta_on_approve && (
        <div className="rounded-md border border-sky-200 bg-sky-50 p-2.5 text-xs text-sky-900" data-testid="decision-meta-sync">
          Approving also sets this item&apos;s title to <b>“{version.meta_on_approve.title}”</b> and updates its description to match the digital diary it was built from.
        </div>
      )}
      {approve && (
        <div><Label className="text-xs">Next review date (default: 12 months)</Label>
          <Input type="date" className="mt-1 w-48" value={nextReview} onChange={(e) => setNextReview(e.target.value)} data-testid="decision-next-review" /></div>
      )}
    </ActionDialog>
  );
}

export function MarkReviewedDialog({ resource, onClose, onDone }) {
  const [notes, setNotes] = useState("");
  const [nextReview, setNextReview] = useState("");
  return (
    <ActionDialog title="Confirm periodic review" testId="mark-reviewed-dialog" confirmLabel="Confirm review" onClose={onClose}
                  description="Use this when the current approved version is still clinically correct. To change the content, upload a new version instead."
                  onConfirm={async () => {
                    await api.post(`/clinical-resources/${resource.id}/mark-reviewed`, { notes, next_review_date: nextReview || null });
                    toast.success("Review recorded");
                    onDone();
                  }}>
      <Notes label="Review notes (optional)" value={notes} onChange={setNotes} testId="mark-reviewed-notes" />
      <div><Label className="text-xs">Next review date (default: 12 months)</Label>
        <Input type="date" className="mt-1 w-48" value={nextReview} onChange={(e) => setNextReview(e.target.value)} data-testid="mark-reviewed-next" /></div>
    </ActionDialog>
  );
}

export function ArchiveDialog({ resource, onClose, onDone }) {
  const [reason, setReason] = useState("");
  return (
    <ActionDialog title="Archive resource" testId="archive-dialog" confirmLabel="Archive" tone="danger" onClose={onClose}
                  canConfirm={reason.trim().length >= 3}
                  description="Archived resources can't be sent to clients. Everything already issued, and its history, is kept."
                  onConfirm={async () => {
                    await api.post(`/clinical-resources/${resource.id}/archive`, { reason });
                    toast.success("Resource archived");
                    onDone();
                  }}>
      <Notes label="Reason" value={reason} onChange={setReason} testId="archive-reason" />
    </ActionDialog>
  );
}
